import BybVrCard from "@Components/common/byb-ds/cards/byb-vr-card";
import TimeDisplay from "@Components/game/progress/stats/time-display";
import { useSessionResultsStaticContext, useSessionResultsTabContext } from "@Context/game/activity/session-results-context";
import { GameModes } from "@Enums/index";
import { getSessionDuration } from "@Helpers/session-utils";
import useI18n from "@i18n";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const TrainingStatsSection = () => {
  const theme = useTheme();
  const { i18n, locale } = useI18n();
  const { currentTab } = useSessionResultsTabContext();
  const { sessionResults } = useSessionResultsStaticContext();

  if (currentTab > 0) {
    return null;
  }

  if (!sessionResults) {
    return (
      <Stack spacing={2} padding={theme.spacing(0, 4)}>
        <Typography variant="text1xl700" color="dsBase.v200">
          {i18n("session-results.training-stats.title")}
        </Typography>

        <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={theme.spacing(3)} height="100%">
          {Array.from({ length: 4 }, (_, index) => (
            <BybVrCard key={index}>
              <Stack spacing={1.5} alignItems="flex-start" justifyContent="center" height="100%">
                <Skeleton variant="text" width={120} height={25} animation="wave" />
                <Skeleton variant="text" width={80} height={36} animation="wave" />
              </Stack>
            </BybVrCard>
          ))}
        </Box>
      </Stack>
    );
  }

  const scenariosCount = sessionResults.play_item_results?.length || 0;
  const totalDuration = getSessionDuration(sessionResults);
  const sessionDate = sessionResults.created_at ? new Date(sessionResults.created_at) : null;

  const durationHours = Math.floor(totalDuration / 3600);
  const durationMinutes = Math.floor((totalDuration % 3600) / 60);
  const durationSeconds = Math.floor(totalDuration % 60);

  const dateDay = sessionDate ? sessionDate.getDate() : null;
  const dateMonthYear = sessionDate
    ? sessionDate.toLocaleDateString(locale, {
        month: "short",
        year: "numeric",
      })
    : null;

  const isMatchPlay = sessionResults.game_mode === GameModes.MATCH_PLAY;
  const sessionType = isMatchPlay ? "matchplay_session" : (sessionResults.session_type || "").toLowerCase();
  const sessionTypeLabel = sessionType ? i18n(`session-type.${sessionType}`) : "--";

  const stats = [
    {
      label: i18n("session-results.training-stats.scenarios"),
      value: scenariosCount.toString(),
      unit: "",
      isTimeDisplay: false,
      isDateDisplay: false,
    },
    {
      label: i18n("session-results.training-stats.total-duration"),
      value: "",
      unit: "",
      isTimeDisplay: true,
      isDateDisplay: false,
      hours: durationHours,
      minutes: durationMinutes,
      seconds: durationSeconds,
    },
    {
      label: i18n("session-results.training-stats.date"),
      value: "",
      unit: "",
      isTimeDisplay: false,
      isDateDisplay: true,
      day: dateDay,
      monthYear: dateMonthYear,
    },
    {
      label: i18n("session-results.training-stats.session-type"),
      value: sessionTypeLabel,
      unit: "",
      isTimeDisplay: false,
      isDateDisplay: false,
    },
  ];

  return (
    <Stack spacing={2} padding={theme.spacing(0, 4)}>
      <Typography variant="text1xl700" color="dsBase.v200">
        {i18n("session-results.training-stats.title")}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: theme.spacing(2),
        }}
      >
        {stats.map((stat, index) => (
          <BybVrCard key={index}>
            <Stack spacing={1.5} alignItems="flex-start" justifyContent="center" height="100%">
              <Typography variant="textLg500" color="text.secondary">
                {stat.label}
              </Typography>
              {stat.isTimeDisplay ? (
                <TimeDisplay hours={stat.hours || 0} minutes={stat.minutes || 0} seconds={stat.seconds || 0} />
              ) : stat.isDateDisplay ? (
                <Stack direction="row" alignItems="baseline" gap={1}>
                  <Typography variant="text3xl700" color="text.primary" textAlign="left">
                    {stat.day || "--"}
                  </Typography>
                  <Typography variant="text1xl400" color="text.secondary">
                    {stat.monthYear || ""}
                  </Typography>
                </Stack>
              ) : (
                <Stack direction="row" alignItems="baseline" spacing={0.5}>
                  <Typography variant="text3xl700" color="text.primary">
                    {stat.value}
                  </Typography>
                  {stat.unit && (
                    <Typography variant="textLg400" color="dsNeutral.v300">
                      {stat.unit}
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>
          </BybVrCard>
        ))}
      </Box>
    </Stack>
  );
};

export default TrainingStatsSection;
